import React, { useEffect, useMemo, useState } from "react";
import { Router } from "react-router-dom";

/**
 * Router for a vendor's own domain. The whole app (links, routes, cart) works with internal paths like
 * /store/<slug>/shop; here the browser shows the clean public path (/shop) and we translate in both directions.
 */
export default function DomainRouter({ slug, children }) {
  const prefix = `/store/${slug}`;
  const toInternal = (path) => {
    if (path === prefix || path.startsWith(`${prefix}/`)) return path; // already internal (e.g. window.location.href links)
    return prefix + (path === "/" ? "" : path);
  };
  const toExternal = (path) => (path === prefix ? "/" : path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path);

  const read = () => ({
    pathname: toInternal(window.location.pathname), search: window.location.search, hash: window.location.hash,
    state: window.history.state?.usr ?? null, key: window.history.state?.key || "default",
  });
  const [location, setLocation] = useState(read);

  useEffect(() => {
    // normalise internal-looking URLs (/store/<slug>/x) to the public form
    const p = window.location.pathname;
    if (p === prefix || p.startsWith(`${prefix}/`)) window.history.replaceState(window.history.state, "", toExternal(p) + window.location.search + window.location.hash);
    const onPop = () => setLocation(read());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigator = useMemo(() => {
    const href = (to) => {
      const t = typeof to === "string" ? new URL(to, "http://x" + window.location.pathname) : { pathname: to.pathname || "/", search: to.search || "", hash: to.hash || "" };
      return toExternal(t.pathname) + (t.search || "") + (t.hash || "");
    };
    const go = (method) => (to, state) => {
      window.history[method]({ usr: state ?? null, key: Math.random().toString(36).slice(2, 10) }, "", href(to));
      setLocation(read());
      if (!window.location.hash) window.scrollTo(0, 0);
    };
    return { createHref: href, push: go("pushState"), replace: go("replaceState"), go: (n) => window.history.go(n) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Router location={location} navigator={navigator}>{children}</Router>;
}
